import React from 'react';
import { motion } from 'framer-motion';
import { 
  Target, 
  CheckCircle, 
  Clock, 
  TrendingUp, 
  AlertTriangle,
  Trophy,
  Star,
  Zap
} from 'lucide-react';
import { ChallengeScenario } from '../types/gameModes';
import { PlayerStats } from '../types/game';

interface ChallengeProgressProps {
  scenario: ChallengeScenario;
  currentAge: number;
  stats: PlayerStats;
  money: number;
  debt: number;
  completedObjectives: string[];
}

const ChallengeProgress: React.FC<ChallengeProgressProps> = ({
  scenario,
  currentAge,
  stats,
  money,
  debt,
  completedObjectives
}) => {
  const getProgressPercentage = () => {
    const totalObjectives = scenario.objectives.length;
    const completed = completedObjectives.length;
    return Math.round((completed / totalObjectives) * 100);
  };

  const getTimeRemaining = () => {
    return Math.max(0, scenario.successConditions.age - currentAge);
  };

  const checkObjectiveStatus = (objective: string) => {
    // Check if objective is completed based on current stats
    if (objective.includes('wealth') && stats.wealth >= 80) return true;
    if (objective.includes('$2,000,000') && money >= 2000000) return true;
    if (objective.includes('$5,000,000') && money >= 5000000) return true;
    if (objective.includes('happiness') && stats.happiness >= 80) return true;
    if (objective.includes('relationships') && stats.relationships >= 85) return true;
    if (objective.includes('debt') && debt <= 0) return true;
    if (objective.includes('health') && stats.health >= 60) return true;
    if (objective.includes('career') && stats.career >= 85) return true;
    return completedObjectives.includes(objective);
  };

  const getDifficultyColor = () => {
    switch (scenario.difficulty) {
      case 'easy': return 'text-green-400 bg-green-400/20';
      case 'medium': return 'text-yellow-400 bg-yellow-400/20';
      case 'hard': return 'text-red-400 bg-red-400/20';
      default: return 'text-slate-400 bg-slate-400/20';
    }
  };

  const getDifficultyIcon = () => {
    switch (scenario.difficulty) {
      case 'easy': return '🟢';
      case 'medium': return '🟡';
      case 'hard': return '🔴';
      default: return '⚪';
    }
  };

  const isChallengeFailed = () => {
    if (scenario.failureConditions) {
      if (scenario.failureConditions.debt && debt >= scenario.failureConditions.debt) return true;
      if (scenario.failureConditions.health && stats.health <= scenario.failureConditions.health) return true;
      if (scenario.failureConditions.age && currentAge >= scenario.failureConditions.age) return true;
    }
    return false;
  };

  const isChallengeCompleted = () => {
    if (currentAge < scenario.successConditions.age) return false;
    if (scenario.successConditions.money && money < scenario.successConditions.money) return false;
    if (scenario.successConditions.debt !== undefined && debt > scenario.successConditions.debt) return false;
    
    // Check stats requirements
    for (const [stat, value] of Object.entries(scenario.successConditions.stats)) {
      if (stats[stat as keyof PlayerStats] < (value as number)) return false;
    }
    
    return true;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-slate-800/50 rounded-2xl border border-slate-700 p-6 mb-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div className={`p-3 rounded-xl bg-gradient-to-r ${scenario.gradient} text-white text-2xl`}>
            {scenario.icon}
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{scenario.name}</h2>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor()}`}>
                {getDifficultyIcon()} {scenario.difficulty}
              </span>
              <span className="text-sm text-slate-400">Challenge Mode</span>
            </div>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-2xl font-bold text-white">{getProgressPercentage()}%</div>
          <div className="text-sm text-slate-400">Complete</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="w-full bg-slate-700 rounded-full h-3">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${getProgressPercentage()}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="bg-gradient-to-r from-orange-500 to-orange-600 h-3 rounded-full"
          />
        </div>
      </div>

      {/* Status Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="flex items-center space-x-3 p-3 bg-slate-700/50 rounded-xl">
          <Clock size={20} className="text-blue-400" />
          <div>
            <div className="text-sm text-slate-400">Time Remaining</div>
            <div className="text-lg font-semibold text-white">{getTimeRemaining()} years</div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3 p-3 bg-slate-700/50 rounded-xl">
          <Target size={20} className="text-orange-400" />
          <div>
            <div className="text-sm text-slate-400">Objectives</div>
            <div className="text-lg font-semibold text-white">
              {completedObjectives.length}/{scenario.objectives.length}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3 p-3 bg-slate-700/50 rounded-xl">
          <TrendingUp size={20} className="text-emerald-400" />
          <div>
            <div className="text-sm text-slate-400">Current Wealth</div>
            <div className="text-lg font-semibold text-white">${money.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Challenge Status */}
      {isChallengeFailed() && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl"
        >
          <div className="flex items-center space-x-3">
            <AlertTriangle size={24} className="text-red-400" />
            <div>
              <h3 className="font-semibold text-red-400">Challenge Failed</h3>
              <p className="text-sm text-red-300">You've met one of the failure conditions. Keep trying!</p>
            </div>
          </div>
        </motion.div>
      )}

      {isChallengeCompleted() && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-xl"
        >
          <div className="flex items-center space-x-3">
            <Trophy size={24} className="text-green-400" />
            <div>
              <h3 className="font-semibold text-green-400">Challenge Completed!</h3>
              <p className="text-sm text-green-300">Congratulations! You've successfully completed this challenge.</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Objectives */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
          <Star size={20} className="text-yellow-400" />
          <span>Objectives</span>
        </h3>
        <div className="space-y-3">
          {scenario.objectives.map((objective, index) => {
            const isCompleted = checkObjectiveStatus(objective);
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center space-x-3 p-3 rounded-xl border-2 ${
                  isCompleted
                    ? 'border-green-500/30 bg-green-500/10'
                    : 'border-slate-600 bg-slate-700/50'
                }`}
              >
                {isCompleted ? (
                  <CheckCircle size={20} className="text-green-400" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-slate-500" />
                )}
                <span className={`text-sm ${
                  isCompleted ? 'text-green-300' : 'text-slate-300'
                }`}>
                  {objective}
                </span>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Tips */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
          <Zap size={20} className="text-yellow-400" />
          <span>Tips</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {scenario.tips.map((tip, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-3 bg-slate-700/50 rounded-xl border border-slate-600"
            >
              <p className="text-sm text-slate-300">{tip}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default ChallengeProgress;
